import './date_range.scss';

import classnames from 'classnames';
import constant from 'lodash/constant';
import moment from 'moment';
import { bool, func, string } from 'prop-types';
import React from 'react';
import { DateRangePicker } from 'react-dates';
import { END_DATE } from 'react-dates/constants';

import { INTERNAL_DATE_FORMAT, UI_DATE_FORMAT } from '../../utils/constants';
import getDatasetFromProps from '../../utils/get-dataset-from-props';

const isDayBlocked = constant(false);
const isOutsideRange = constant(false);

function dateChanged(newMoment, previousMoment) {
  return newMoment
    ? !newMoment.isSame(previousMoment)
    : previousMoment !== newMoment;
}

function recalculateEndDate(
  startDate,
  prevStartDate,
  prevEndDate,
  focusedInput,
) {
  // start date cleared -> do nothing to the end date
  if (!startDate) {
    return prevEndDate;
  }
  // end date focus -> duration until the end of the month because end was set before start
  // no end date -> end of the month
  if (focusedInput === END_DATE || !prevEndDate) {
    return moment(startDate).endOf('month');
  }

  // no duration before -> keep end date
  if (!prevStartDate) {
    return prevEndDate;
  }

  // existing end date -> keep duration but move
  const duration = prevEndDate.diff(prevStartDate, 'days');
  return moment(startDate).add(duration, 'days');
}

class DateRange extends React.Component {
  constructor(props) {
    super(props);

    this.state = { focusedInput: null };

    this.handleChange = this.handleChange.bind(this);
    this.handleFocusChange = this.handleFocusChange.bind(this);
  }

  handleFocusChange(focusedInput) {
    this.setState({ focusedInput });
  }

  handleChange({ startDate, endDate }) {
    const { startName, endName } = this.props;
    const prevStartDate = this.props.startDate && moment(this.props.startDate);
    const prevEndDate = this.props.endDate && moment(this.props.endDate);

    if (dateChanged(startDate, prevStartDate)) {
      this.triggerChange(startName, startDate);

      const newEndDate = recalculateEndDate(
        startDate,
        prevStartDate,
        prevEndDate,
        this.state.focusedInput,
      );
      if (dateChanged(newEndDate, prevEndDate)) {
        this.triggerChange(endName, newEndDate);
      }
    } else if (dateChanged(endDate, prevEndDate)) {
      this.triggerChange(endName, endDate);
    }
  }

  triggerChange(name, date) {
    this.props.onChange({
      target: {
        name,
        value: date && date.format(INTERNAL_DATE_FORMAT),
        dataset: getDatasetFromProps(this.props),
      },
    });
  }

  render() {
    const { focusedInput } = this.state;

    const {
      startDate,
      endDate,
      startName,
      endName,
      onChange,
      id,
      readOnly,
      text,
      className,
      ...rest
    } = this.props;

    const datePickerClasses = classnames(className, {
      'c-date-range--plain-text': text,
    });

    return (
      <div className={datePickerClasses}>
        <DateRangePicker
          displayFormat={UI_DATE_FORMAT}
          isDayBlocked={isDayBlocked}
          isOutsideRange={isOutsideRange}
          disabled={readOnly}
          minimumNights={0}
          {...rest}
          focusedInput={focusedInput}
          startDate={startDate && moment(startDate)}
          startDateId={`${id}-${startName}`}
          endDateId={`${id}-${endName}`}
          endDate={endDate && moment(endDate)}
          onDatesChange={this.handleChange}
          onFocusChange={this.handleFocusChange}
        />
      </div>
    );
  }
}

DateRange.propTypes = {
  endDate: string,
  endName: string.isRequired,
  id: string,
  onChange: func,
  readOnly: bool,
  startDate: string,
  startName: string.isRequired,
  text: bool,
  className: string,
};

DateRange.defaultProps = {
  endDate: null,
  id: undefined,
  onChange: () => {},
  readOnly: undefined,
  startDate: null,
  text: false,
  className: '',
};

export default DateRange;
